import React, { useEffect } from "react";
import { Box, Typography } from "@material-ui/core";
import { DateRangePicker, PickerType } from "halifax";
import dayjs from "dayjs";

import "./styles.scss";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { CallState } from "redmond";
import { RouteComponentProps } from "react-router";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import clsx from "clsx";
import { parseQueryString } from "../../utils/parseQueryString";
import {
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { MonthAndDatePickerConnectorProps } from "./container";

export interface IMonthAndDatePickerProps
  extends MonthAndDatePickerConnectorProps,
    RouteComponentProps {
  pickerType: PickerType;
  viewType: MonthAndDatePickerType;
  header?: JSX.Element | string;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  focusedMonthIndex?: number;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  className?: string;
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    pickerType,
    startDate,
    endDate,
    months,
    priceTags,
    setStartDate,
    setEndDate,
    viewType,
    header,
    focusedMonthIndex,
    setFocusedMonthIndex,
    className,
    minAllowedDate,
    maxAllowedDate,
    priceBucketsLoadingCallState,
    searchedOrigin,
    history,
    hasOriginAutocompleteError,
    userFlightPreferences,
    userFlightPreferencesCallState,
  } = props;

  const parsedQueryString = parseQueryString(history);
  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(
        priceBucketsLoadingCallState
      ) ||
      (!searchedOrigin &&
        (!parsedQueryString?.origin
          ? userFlightPreferencesCallState === CallState.Success &&
            !userFlightPreferences?.homeAirport
          : hasOriginAutocompleteError))
    ) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.CALENDAR_LOADED,
        funnel: TravelAppInterfaceFunnelType.FLIGHTS,
      });
    }
  }, [
    priceBucketsLoadingCallState,
    parsedQueryString?.origin,
    hasOriginAutocompleteError,
    userFlightPreferencesCallState,
    userFlightPreferences?.homeAirport,
  ]);

  return (
    <>
      {header && (
        <Box className="month-and-date-picker-header-container">
          {typeof header === "string" ? (
            <Typography
              variant="h3"
              className="month-and-date-picker-header-text"
            >
              {header}
            </Typography>
          ) : (
            header
          )}
        </Box>
      )}
      <DateRangePicker
        className={clsx(className, { xmod: isXmodRedesign })}
        minAllowedDate={minAllowedDate || dayjs().toDate()}
        maxAllowedDate={maxAllowedDate || dayjs().add(1, "year").toDate()}
        months={months}
        priceTags={priceTags}
        startDate={startDate}
        endDate={endDate}
        usePriceTagSymbols={isXmodRedesign}
        showDayPriceSymbols={isXmodRedesign}
        showOverPricingText={!isXmodRedesign}
        trimLegendValues={!isXmodRedesign}
        showFullMonthName={isXmodRedesign}
        {...(viewType === MonthAndDatePickerType.Horizontal ||
        viewType === MonthAndDatePickerType.Column
          ? { setStartDate, setEndDate, pickerType }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Column
          ? { focusedMonthIndex, columnView: true }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Group
          ? { setFocusedMonthIndex, groupView: true }
          : undefined)}
      />
    </>
  );
};

export enum MonthAndDatePickerType {
  Horizontal,
  Column,
  Group,
}
